import { useEffect, useRef } from 'react';

import { ApolloClient } from '@apollo/client';

import {
  GetLoyaltyStateQuery,
  LoyaltyStateEnum,
} from '@notino/shared/definitions/types';

import getLoyaltyState from '../queries/loyaltyState.graphql';

interface UseLoyaltyPollingArgs {
  client: ApolloClient<object>;
  onCompleted: (data: LoyaltyStateEnum) => void;
}

export const useLoyaltyPolling = ({
  client,
  onCompleted,
}: UseLoyaltyPollingArgs) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);
  const initialStateRef = useRef<LoyaltyStateEnum | null>(null);
  const stoppedRef = useRef(false);

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    stoppedRef.current = true;
  };

  const startPolling = () => {
    stoppedRef.current = false;
    inFlightRef.current = false;
    initialStateRef.current = null;

    const tick = async () => {
      if (stoppedRef.current || inFlightRef.current) return;

      inFlightRef.current = true;
      try {
        const res = await client.query<GetLoyaltyStateQuery>({
          query: getLoyaltyState,
          fetchPolicy: 'network-only',
        });

        if (stoppedRef.current) return;

        const nextState = res.data.getLoyaltyState?.state ?? null;

        if (nextState === null) {
          return;
        }

        if (initialStateRef.current === null) {
          initialStateRef.current = nextState;
        } else if (nextState !== initialStateRef.current) {
          stopPolling();
          onCompleted(nextState);
        }
      } catch {
        // ignore error
      } finally {
        inFlightRef.current = false;
      }
    };

    void tick();
    intervalRef.current = setInterval(() => void tick(), 2000);
  };

  useEffect(() => {
    return () => stopPolling();
  }, []);

  return { startPolling, stopPolling };
};
